import type { AccountOption, LabRiskSettingsModel, PendingRuleDeletion } from '.';
import type { RiskLimitField, RiskLimits } from './limits';
import { riskLimitsEqual } from './limits';
import type {
    AccountRiskRuleRow,
    AdvancedRiskRule,
    AdvancedRuleFilter,
    ProviderRiskRuleRow,
    SymbolRiskRule,
} from './rules';
import { classifyRiskParameter, filterAdvancedRules, riskLimitsOfParameter } from './rules';
import {
    applySavedDefaults as applySavedDefaultsAction,
    cancelDeleteRule as cancelDeleteRuleAction,
    closeRuleDialog as closeRuleDialogAction,
    EMPTY_RULE_DRAFT,
    failSaveDefaults as failSaveDefaultsAction,
    failSaveRule as failSaveRuleAction,
    findRuleSymbolConflicts,
    finishDeleteRule as finishDeleteRuleAction,
    initialLabRiskSettingsState,
    labRiskSettingsReducer,
    openRuleDialog as openRuleDialogAction,
    parseFirmDefaultsDraft,
    parseRiskRuleDraft,
    parseScopedRuleDraft,
    patchRuleDraft as patchRuleDraftAction,
    requestDeleteRule as requestDeleteRuleAction,
    setActiveTab as setActiveTabAction,
    setAdvancedFilter as setAdvancedFilterAction,
    setAdvancedSearchInput as setAdvancedSearchInputAction,
    setDefaultEnabled as setDefaultEnabledAction,
    setDefaultValue as setDefaultValueAction,
    setFirm as setFirmAction,
    startDeleteRule as startDeleteRuleAction,
    startSaveDefaults as startSaveDefaultsAction,
    startSaveRule as startSaveRuleAction,
    syncSavedDefaults as syncSavedDefaultsAction,
} from './state';
import type {
    FirmDefaultsDraft,
    LabRiskSettingsAction,
    LabRiskSettingsState,
    LabRiskSettingsTab,
    RiskRuleDialogState,
    RiskRuleDraft,
    RiskRuleScope,
} from './state';
import { effect } from '@preact/signals-core';
import { computed, signal } from '@preact/signals-react';
import type { ReadonlySignal } from '@preact/signals-react';
import type { QueryObserverOptions } from '@tanstack/query-core';
import type {
    FirmRiskResponse,
    RiskParameter,
    RiskParameterData,
    RiskParametersResponse,
} from '@thinkalpha/locates-ws-client';
import type { ConcreteAccount } from '@thinkalpha/trading-ws-client/contracts/account.js';
import { t } from 'i18next';
import type { Provider } from 'src/admin/LAB/contracts/providers';
import { downloadCsvRows } from 'src/admin/LAB/csv/downloadCsv';
import { log } from 'src/admin/LAB/log';
import { buildAdvancedRulesCsvRows } from 'src/admin/LAB/RiskSettings/advancedRulesCsv';
import { inject, injectable } from 'src/features/ioc';
import type { QueryObserverModel } from 'src/models/QueryObserverModel';
import { getAccountsForSubjectQuery } from 'src/queries/accounts';
import { getAllProvidersQuery } from 'src/queries/labProviders';
import {
    getFirmRiskQuery,
    getRiskParametersQuery,
    invalidateFirmRiskQuery,
    invalidateRiskParametersQuery,
} from 'src/queries/labRisk';
import {
    createRiskParameter,
    deleteRiskParameter,
    putFirmRisk,
    updateRiskParameter,
} from 'src/queries/labRiskMutations';
import type { ReactBindings } from 'src/types/bindings';

@injectable()
export class LabRiskSettingsModelImpl implements LabRiskSettingsModel {
    readonly #disposableStack = new DisposableStack();
    readonly #state = signal<LabRiskSettingsState>(initialLabRiskSettingsState);
    readonly #firmName = signal<string | null>(null);
    /**
     * Flips once `setFirm` runs. Gates the superuser-capable query so the
     * unscoped fetch cannot fire during the synchronous window between
     * construction and the route's `setFirm` call.
     */
    readonly #firmScopeSet = signal(false);
    /** Set on disposal so async save/delete continuations bail out. */
    readonly #abort = new AbortController();
    readonly #queryClient: ReactBindings['QueryClient'];
    readonly #store: ReactBindings['Store'];
    readonly #currentUser: ReactBindings['CurrentUserModel'];

    /**
     * Superuser access is not part of the model's scope: it propagates
     * reactively from the current-user model, so a mode toggle flows
     * into the query gates without rebuilding or rescoping anything.
     */
    readonly #superUserMode: ReadonlySignal<boolean>;
    /**
     * Authorization for the provider list: a firm scope must be set, and
     * the unscoped (all-firms) list is superuser-only. Gates the query and
     * its projection alike, since a disabled query keeps warm cache data.
     */
    readonly #authorized: ReadonlySignal<boolean>;
    readonly #providersQuery: QueryObserverModel<Provider[], Error, Provider[]>;
    readonly #accountsQuery: QueryObserverModel<ConcreteAccount[], Error, ConcreteAccount[]>;
    readonly #firmRiskQuery: QueryObserverModel<FirmRiskResponse, Error, FirmRiskResponse>;
    readonly #parametersQuery: QueryObserverModel<RiskParametersResponse, Error, RiskParametersResponse>;

    constructor(
        @inject('QueryObserverModelFactory') queryObserverModelFactory: ReactBindings['QueryObserverModelFactory'],
        @inject('QueryClient') queryClient: ReactBindings['QueryClient'],
        @inject('Store') store: ReactBindings['Store'],
        @inject('CurrentUserModel') currentUser: ReactBindings['CurrentUserModel'],
    ) {
        this.#queryClient = queryClient;
        this.#store = store;
        this.#currentUser = currentUser;
        // Initialized here, after `#currentUser` is assigned, rather than as
        // a field initializer that would depend on `computed` staying lazy.
        this.#superUserMode = computed(
            () => this.#currentUser.isSuperuser.value && this.#currentUser.isSuperUserModeEnabled.value,
        );
        this.#authorized = computed(
            () => this.#firmScopeSet.value && (this.#superUserMode.value || Boolean(this.#state.value.firmId)),
        );

        const providersOptions = computed<QueryObserverOptions<Provider[], Error, Provider[]>>(() => {
            const firmId = this.#state.value.firmId;
            return {
                ...getAllProvidersQuery(firmId),
                // Mirrors `useAllProviders`: superusers can list without a firm.
                enabled: this.#authorized.value,
            };
        });
        this.#providersQuery = queryObserverModelFactory<Provider[], Error, Provider[]>(providersOptions);
        this.#disposableStack.use(this.#providersQuery);

        // Account rules need a firm scope; in the superuser all-firms view
        // the select stays empty until a firm is opened.
        const accountsOptions = computed<QueryObserverOptions<ConcreteAccount[], Error, ConcreteAccount[]>>(() =>
            getAccountsForSubjectQuery(this.#state.value.firmId, 'firm'),
        );
        this.#accountsQuery = queryObserverModelFactory<ConcreteAccount[], Error, ConcreteAccount[]>(accountsOptions);
        this.#disposableStack.use(this.#accountsQuery);

        const firmRiskOptions = computed<QueryObserverOptions<FirmRiskResponse, Error, FirmRiskResponse>>(() =>
            getFirmRiskQuery(this.#state.value.firmId),
        );
        this.#firmRiskQuery = queryObserverModelFactory<FirmRiskResponse, Error, FirmRiskResponse>(firmRiskOptions);
        this.#disposableStack.use(this.#firmRiskQuery);

        const parametersOptions = computed<QueryObserverOptions<RiskParametersResponse, Error, RiskParametersResponse>>(
            () => getRiskParametersQuery(this.#state.value.firmId),
        );
        this.#parametersQuery = queryObserverModelFactory<RiskParametersResponse, Error, RiskParametersResponse>(
            parametersOptions,
        );
        this.#disposableStack.use(this.#parametersQuery);

        // Keep the firm defaults baseline in step with the server. The
        // draft only resets while it's clean so an in-progress edit
        // survives background refetches.
        this.#disposableStack.defer(
            effect(() => {
                const response = this.#firmRiskQuery.signal.value.data;
                if (!response) return;
                const limits: RiskLimits = {
                    maxPrice: response.parameter.maxPrice,
                    maxSize: response.parameter.maxSize,
                    maxOrderTotal: response.parameter.maxOrderTotal,
                    maxDailySize: response.parameter.maxDailySize,
                };
                const state = this.#state.value;
                if (riskLimitsEqual(limits, state.savedDefaults)) return;
                const parse = parseFirmDefaultsDraft(state.draftDefaults);
                const clean = !parse.hasInvalidInput && riskLimitsEqual(parse.limits, state.savedDefaults);
                this.#dispatch(syncSavedDefaultsAction(limits, clean));
            }),
        );
    }

    #dispatch(action: LabRiskSettingsAction): void {
        this.#state.value = labRiskSettingsReducer(this.#state.value, action);
    }

    // An active tab that got hidden (the Advanced tab with no rules left)
    // falls back to the default rather than pointing at nothing.
    readonly activeTab: ReadonlySignal<LabRiskSettingsTab> = computed(() => {
        const tab = this.#state.value.activeTab;
        return tab === 'advanced-settings' && !this.showAdvancedSettingsTab.value ? 'firm-defaults' : tab;
    });
    readonly firmDefaultsDraft: ReadonlySignal<FirmDefaultsDraft> = computed(() => this.#state.value.draftDefaults);
    readonly savedFirmDefaults: ReadonlySignal<RiskLimits> = computed(() => this.#state.value.savedDefaults);
    readonly isSavingDefaults: ReadonlySignal<boolean> = computed(() => this.#state.value.isSavingDefaults);
    readonly defaultsLoading: ReadonlySignal<boolean> = computed(() => this.#firmRiskQuery.signal.value.isLoading);
    readonly defaultsError: ReadonlySignal<boolean> = computed(() => this.#firmRiskQuery.signal.value.isError);

    readonly #draftParse = computed(() => parseFirmDefaultsDraft(this.#state.value.draftDefaults));

    // A non-positive value counts as dirty (the user changed something) but blocks saving.
    readonly isFirmDefaultsDirty: ReadonlySignal<boolean> = computed(() => {
        const parse = this.#draftParse.value;
        return parse.hasInvalidInput || !riskLimitsEqual(parse.limits, this.#state.value.savedDefaults);
    });
    readonly canSaveFirmDefaults: ReadonlySignal<boolean> = computed(
        () =>
            this.isFirmDefaultsDirty.value &&
            !this.#draftParse.value.hasInvalidInput &&
            !this.#state.value.isSavingDefaults &&
            // The PUT replaces the whole rule, so saving before the current
            // rule is known would clear limits this form does not edit.
            this.#firmRiskQuery.signal.value.data !== undefined,
    );

    readonly rulesLoading: ReadonlySignal<boolean> = computed(() => this.#parametersQuery.signal.value.isLoading);
    readonly rulesError: ReadonlySignal<boolean> = computed(() => this.#parametersQuery.signal.value.isError);

    readonly #parameters: ReadonlySignal<readonly RiskParameter[]> = computed(
        () => this.#parametersQuery.signal.value.data?.parameters ?? [],
    );

    /** Find a rule the UI can act on; advanced rules are read-only in this UI. */
    #findActionableParameter(parameterId: string): { parameter: RiskParameter; kind: RiskRuleScope } | null {
        const parameter = this.#parameters.value.find((candidate) => candidate.id === parameterId);
        if (!parameter) return null;
        const kind = classifyRiskParameter(parameter);
        if (kind === null || kind === 'advanced') return null;
        return { parameter, kind };
    }

    /** Display name of a scoped rule's provider or account; falls back to the raw id. */
    #scopeNameOf(parameter: RiskParameter, kind: RiskRuleScope): string | null {
        switch (kind) {
            case 'symbol':
                return null;
            case 'provider':
                return this.#providerNameById.value.get(parameter.providerId ?? '') ?? parameter.providerId ?? '';
            case 'account':
                return this.#accountById.value.get(parameter.accountId ?? '')?.name ?? parameter.accountId ?? '';
        }
    }

    readonly symbolRules: ReadonlySignal<readonly SymbolRiskRule[]> = computed(() =>
        this.#parameters.value
            // classifyRiskParameter only yields 'symbol' for a non-null
            // symbol; the null check narrows the type instead of a fallback.
            .flatMap((parameter): SymbolRiskRule[] =>
                classifyRiskParameter(parameter) !== 'symbol' || parameter.symbol === null
                    ? []
                    : [{ id: parameter.id, symbol: parameter.symbol, ...riskLimitsOfParameter(parameter) }],
            )
            .toSorted((a, b) => a.symbol.localeCompare(b.symbol)),
    );

    readonly providers: ReadonlySignal<ReadonlyArray<Provider>> = computed(() =>
        this.#authorized.value ? (this.#providersQuery.signal.value.data ?? []) : [],
    );
    readonly #providerNameById = computed(
        () => new Map(this.providers.value.map((provider): [string, string] => [provider.id, provider.name])),
    );
    // Names are resolved at render time so the rows survive the provider
    // list loading later; unresolved ids fall back to the raw id.
    readonly providerRuleRows: ReadonlySignal<readonly ProviderRiskRuleRow[]> = computed(() =>
        this.#parameters.value
            .filter((parameter) => classifyRiskParameter(parameter) === 'provider')
            .map(
                (parameter): ProviderRiskRuleRow => ({
                    id: parameter.id,
                    providerId: parameter.providerId ?? '',
                    symbol: parameter.symbol,
                    providerName: this.#scopeNameOf(parameter, 'provider') ?? '',
                    ...riskLimitsOfParameter(parameter),
                }),
            )
            // All-symbols (null) rules sort before concrete symbols.
            .toSorted(
                (a, b) =>
                    a.providerName.localeCompare(b.providerName) || (a.symbol ?? '').localeCompare(b.symbol ?? ''),
            ),
    );

    readonly accounts: ReadonlySignal<readonly AccountOption[]> = computed(() => {
        const data = this.#accountsQuery.signal.value.data ?? [];
        return data
            .map(
                (account): AccountOption => ({
                    id: account.id,
                    // The backend can return an empty displayName; treat it
                    // like a missing one so the code still shows as the name.
                    name: account.displayName && account.displayName.length > 0 ? account.displayName : account.name,
                    code: account.name,
                }),
            )
            .toSorted((a, b) => a.name.localeCompare(b.name));
    });
    readonly #accountById = computed(
        () => new Map(this.accounts.value.map((account): [string, AccountOption] => [account.id, account])),
    );
    readonly accountRuleRows: ReadonlySignal<readonly AccountRiskRuleRow[]> = computed(() => {
        const byId = this.#accountById.value;
        return (
            this.#parameters.value
                .filter((parameter) => classifyRiskParameter(parameter) === 'account')
                .map((parameter): AccountRiskRuleRow => {
                    const account = byId.get(parameter.accountId ?? '');
                    return {
                        id: parameter.id,
                        accountId: parameter.accountId ?? '',
                        symbol: parameter.symbol,
                        accountName: account?.name ?? parameter.accountId ?? '',
                        accountCode: account?.code ?? '',
                        ...riskLimitsOfParameter(parameter),
                    };
                })
                // All-symbols (null) rules sort before concrete symbols.
                .toSorted(
                    (a, b) =>
                        a.accountName.localeCompare(b.accountName) || (a.symbol ?? '').localeCompare(b.symbol ?? ''),
                )
        );
    });

    readonly #advancedRules: ReadonlySignal<readonly AdvancedRiskRule[]> = computed(() => {
        const providerNames = this.#providerNameById.value;
        const accounts = this.#accountById.value;
        const firm = this.#firmName.value ?? this.#state.value.firmId ?? '';
        return this.#parameters.value
            .filter((parameter) => classifyRiskParameter(parameter) === 'advanced')
            .map(
                (parameter): AdvancedRiskRule => ({
                    id: parameter.id,
                    firm,
                    // The wire has no member firm or route dimensions (yet);
                    // they render as N/A. Every stored rule is in force, so
                    // the Enabled column is truthfully constant.
                    memberFirm: null,
                    route: null,
                    account: accounts.get(parameter.accountId ?? '')?.name ?? parameter.accountId,
                    provider: providerNames.get(parameter.providerId ?? '') ?? parameter.providerId,
                    symbol: parameter.symbol,
                    enabled: true,
                    ...riskLimitsOfParameter(parameter),
                }),
            );
    });
    readonly advancedRuleRows: ReadonlySignal<readonly AdvancedRiskRule[]> = computed(() => {
        const { advancedFilter, advancedSearchInput } = this.#state.value;
        return filterAdvancedRules(this.#advancedRules.value, advancedFilter, advancedSearchInput);
    });
    readonly advancedRuleTotalCount: ReadonlySignal<number> = computed(() => this.#advancedRules.value.length);
    readonly showAdvancedSettingsTab: ReadonlySignal<boolean> = computed(() => this.advancedRuleTotalCount.value > 0);
    readonly advancedFilter: ReadonlySignal<AdvancedRuleFilter> = computed(() => this.#state.value.advancedFilter);
    readonly advancedSearchInput: ReadonlySignal<string> = computed(() => this.#state.value.advancedSearchInput);

    readonly ruleDialog: ReadonlySignal<RiskRuleDialogState | null> = computed(() => this.#state.value.ruleDialog);
    // Resolved at render time like the rule rows; the dialog closes by
    // itself when the rule disappears (e.g. a refetch after the delete).
    readonly pendingRuleDeletion: ReadonlySignal<PendingRuleDeletion | null> = computed(() => {
        const pending = this.#state.value.pendingRuleDeletion;
        if (pending === null) return null;
        const found = this.#findActionableParameter(pending.parameterId);
        if (found === null) return null;
        const { parameter, kind } = found;
        return {
            parameterId: parameter.id,
            scope: kind,
            scopeName: this.#scopeNameOf(parameter, kind),
            symbol: parameter.symbol,
            isDeleting: pending.isDeleting,
        };
    });
    // The backend enforces one rule per firm/provider/account/symbol;
    // surfacing conflicts here blocks the save instead of letting the
    // create fail with an opaque server error.
    readonly conflictingRuleSymbols: ReadonlySignal<ReadonlyArray<string | null>> = computed(() => {
        const dialog = this.#state.value.ruleDialog;
        if (dialog === null) return [];
        return findRuleSymbolConflicts(this.#parameters.value, dialog);
    });

    readonly canSaveRule: ReadonlySignal<boolean> = computed(() => {
        const dialog = this.#state.value.ruleDialog;
        // Rules always belong to a firm; in the superuser all-firms view
        // there is nothing to save onto.
        if (dialog === null || dialog.isSaving || !this.#state.value.firmId) return false;
        if (this.conflictingRuleSymbols.value.length > 0) return false;
        if (dialog.scope === 'symbol') {
            // All-symbols would make the rule a firm default, which the
            // backend refuses; a symbol rule needs concrete symbols.
            const parsed = parseRiskRuleDraft(dialog.draft);
            return parsed !== null && parsed.symbols.length > 0;
        }
        return parseScopedRuleDraft(dialog.draft) !== null;
    });

    setFirm(firmId: string | undefined): void {
        if (this.#firmScopeSet.value) {
            if (this.#state.value.firmId === firmId) return;
            throw new Error(
                'LabRiskSettingsModel firm scope is fixed after initialization; create a new instance for a different firm',
            );
        }
        // Dispatch first so the queries see the scoped firm and the gate
        // flip in one consistent read.
        this.#dispatch(setFirmAction(firmId));
        this.#firmScopeSet.value = true;
    }

    setFirmName(firmName: string | null): void {
        this.#firmName.value = firmName;
    }

    setActiveTab(tab: LabRiskSettingsTab): void {
        this.#dispatch(setActiveTabAction(tab));
    }

    setFirmDefaultEnabled(field: RiskLimitField, enabled: boolean): void {
        this.#dispatch(setDefaultEnabledAction(field, enabled));
    }

    setFirmDefaultValue(field: RiskLimitField, value: number | null): void {
        this.#dispatch(setDefaultValueAction(field, value));
    }

    async saveFirmDefaults(): Promise<void> {
        const firmId = this.#state.value.firmId;
        if (!this.canSaveFirmDefaults.value || !firmId) return;
        const { limits } = this.#draftParse.value;
        this.#dispatch(startSaveDefaultsAction());
        try {
            const response = await putFirmRisk(firmId, limits);
            // A firm switch disposed this instance mid-save: the write is
            // the backend's now; nothing may land on the dead instance.
            if (this.#abort.signal.aborted) return;
            this.#dispatch(
                applySavedDefaultsAction({
                    maxPrice: response.parameter.maxPrice,
                    maxSize: response.parameter.maxSize,
                    maxOrderTotal: response.parameter.maxOrderTotal,
                    maxDailySize: response.parameter.maxDailySize,
                }),
            );
            this.#store.dispatch({
                type: 'toast::enqueue',
                toast: {
                    message: t('admin::lab::risk-settings::save-defaults-success', 'Firm defaults saved.'),
                    group: 'default',
                    variant: 'success',
                },
            });
        } catch (error) {
            log.error(`Saving LAB firm risk defaults failed for firm ${firmId}`, { error, firmId, limits });
            if (this.#abort.signal.aborted) return;
            this.#dispatch(failSaveDefaultsAction());
            this.#store.dispatch({
                type: 'toast::enqueue',
                toast: {
                    message: t(
                        'admin::lab::risk-settings::save-defaults-error',
                        'Saving the firm defaults failed. Please try again.',
                    ),
                    group: 'default',
                    variant: 'error',
                },
            });
            return;
        }
        // The write succeeded; a refetch failure must not be reported as
        // a save failure.
        try {
            await invalidateFirmRiskQuery(this.#queryClient, firmId);
        } catch (error) {
            log.error(`Refreshing LAB firm risk defaults failed for firm ${firmId}`, { error, firmId });
        }
    }

    #openCreateDialog(scope: RiskRuleScope): void {
        this.#dispatch(
            openRuleDialogAction({
                scope,
                mode: 'create',
                editingId: null,
                draft: EMPTY_RULE_DRAFT,
                isSaving: false,
                error: null,
            }),
        );
    }

    openCreateSymbolRule(): void {
        this.#openCreateDialog('symbol');
    }

    openCreateProviderRule(): void {
        this.#openCreateDialog('provider');
    }

    openCreateAccountRule(): void {
        this.#openCreateDialog('account');
    }

    openEditRule(parameterId: string): void {
        const found = this.#findActionableParameter(parameterId);
        if (found === null) return;
        const { parameter, kind } = found;
        this.#dispatch(
            openRuleDialogAction({
                scope: kind,
                mode: 'edit',
                editingId: parameter.id,
                draft: {
                    scopeId: parameter.accountId ?? parameter.providerId ?? '',
                    // An all-symbols rule edits as an empty symbol list.
                    symbols: parameter.symbol === null ? [] : [parameter.symbol],
                    ...riskLimitsOfParameter(parameter),
                },
                isSaving: false,
                error: null,
            }),
        );
    }

    closeRuleDialog(): void {
        // Ignored while a save is in flight so the dialog can't vanish
        // out from under the pending request.
        if (this.#state.value.ruleDialog?.isSaving) return;
        this.#dispatch(closeRuleDialogAction());
    }

    updateRuleDraft(patch: Partial<RiskRuleDraft>): void {
        this.#dispatch(patchRuleDraftAction(patch));
    }

    addRuleSymbol(symbol: string): void {
        const dialog = this.#state.value.ruleDialog;
        if (dialog === null) return;
        const normalized = symbol.trim().toUpperCase();
        if (normalized === '' || normalized.includes('*')) return;
        // Editing targets one stored rule, so a pick replaces the pill.
        const symbols =
            dialog.mode === 'edit'
                ? [normalized]
                : dialog.draft.symbols.includes(normalized)
                  ? dialog.draft.symbols
                  : [...dialog.draft.symbols, normalized];
        this.#dispatch(patchRuleDraftAction({ symbols }));
    }

    removeRuleSymbol(symbol: string): void {
        const dialog = this.#state.value.ruleDialog;
        if (dialog === null) return;
        this.#dispatch(
            patchRuleDraftAction({ symbols: dialog.draft.symbols.filter((candidate) => candidate !== symbol) }),
        );
    }

    /** The dialog's provider/account target; `null` for firm-level symbol rules. */
    #ruleTarget(dialog: RiskRuleDialogState): Pick<RiskParameterData, 'accountId' | 'providerId'> {
        switch (dialog.scope) {
            case 'symbol':
                return { accountId: null, providerId: null };
            case 'provider':
                return { accountId: null, providerId: dialog.draft.scopeId };
            case 'account':
                return { accountId: dialog.draft.scopeId, providerId: null };
        }
    }

    async saveRule(): Promise<void> {
        const dialog = this.#state.value.ruleDialog;
        const firmId = this.#state.value.firmId;
        if (dialog === null || !firmId || !this.canSaveRule.value) return;
        const parsed =
            dialog.scope === 'symbol' ? parseRiskRuleDraft(dialog.draft) : parseScopedRuleDraft(dialog.draft);
        if (parsed === null) return;
        // A firm-level symbol rule must name concrete symbols; all-symbols
        // would be a firm default, which the backend refuses. canSaveRule
        // already blocks this; the re-check keeps direct calls safe.
        if (dialog.scope === 'symbol' && parsed.symbols.length === 0) {
            this.#dispatch(
                failSaveRuleAction(
                    t(
                        'admin::lab::risk-settings::rule-symbol-required-error',
                        'A symbol rule needs a concrete symbol.',
                    ),
                ),
            );
            return;
        }
        const target = this.#ruleTarget(dialog);
        const limits: RiskLimits = {
            maxPrice: parsed.maxPrice,
            maxSize: parsed.maxSize,
            maxOrderTotal: parsed.maxOrderTotal,
            maxDailySize: parsed.maxDailySize,
        };
        // One rule per picked symbol; an empty list is the all-symbols rule.
        const symbols: ReadonlyArray<string | null> = parsed.symbols.length === 0 ? [null] : parsed.symbols;
        this.#dispatch(startSaveRuleAction());
        if (dialog.editingId !== null) {
            // Editing is constrained to a single symbol (see addRuleSymbol).
            const payload = { ...target, symbol: symbols[0], ...limits } satisfies RiskParameterData;
            try {
                await updateRiskParameter(firmId, dialog.editingId, payload);
            } catch (error) {
                log.error(`Saving LAB risk rule failed for firm ${firmId}`, { error, firmId, payload });
                if (this.#abort.signal.aborted) return;
                // The dialog is modal and the firm scope is fixed for the
                // model's lifetime, so this failure always belongs to the
                // dialog that started the save.
                this.#dispatch(
                    failSaveRuleAction(
                        t('admin::lab::risk-settings::save-rule-error', 'Saving the rule failed. Please try again.'),
                    ),
                );
                return;
            }
        } else {
            const results = await Promise.allSettled(
                symbols.map((symbol) => createRiskParameter(firmId, { ...target, symbol, ...limits })),
            );
            if (this.#abort.signal.aborted) return;
            const failed = symbols.filter((_, index) => results[index].status === 'rejected');
            if (failed.length > 0) {
                log.error(`Saving ${failed.length} of ${symbols.length} LAB risk rules failed for firm ${firmId}`, {
                    firmId,
                    failed,
                });
                // Keep only the failed symbols so a retry doesn't duplicate
                // the rules that were created.
                this.#dispatch(
                    patchRuleDraftAction({
                        symbols: failed.filter((symbol): symbol is string => symbol !== null),
                    }),
                );
                this.#dispatch(
                    failSaveRuleAction(
                        t('admin::lab::risk-settings::save-rule-error', 'Saving the rule failed. Please try again.'),
                    ),
                );
                // The created rules are real; refresh so the tables show them.
                await this.#refreshRiskQueries(firmId);
                return;
            }
        }
        if (this.#abort.signal.aborted) return;
        // The write succeeded; a refetch failure must not keep the dialog
        // open as a retryable save failure — retrying would duplicate the
        // rules in create mode.
        this.#dispatch(closeRuleDialogAction());
        await this.#refreshRiskQueries(firmId);
    }

    /** Refresh both risk caches after a successful write; failures only log. */
    async #refreshRiskQueries(firmId: string): Promise<void> {
        try {
            await Promise.all([
                invalidateRiskParametersQuery(this.#queryClient, firmId),
                invalidateFirmRiskQuery(this.#queryClient, firmId),
            ]);
        } catch (error) {
            log.error(`Refreshing LAB risk rules failed for firm ${firmId}`, { error, firmId });
        }
    }

    #lastDeleteRuleToken = 0;

    requestDeleteRule(parameterId: string): void {
        if (this.#findActionableParameter(parameterId) === null) return;
        this.#dispatch(requestDeleteRuleAction(parameterId, ++this.#lastDeleteRuleToken));
    }

    cancelDeleteRule(): void {
        this.#dispatch(cancelDeleteRuleAction());
    }

    async confirmDeleteRule(): Promise<void> {
        const { firmId, pendingRuleDeletion } = this.#state.value;
        if (!firmId || pendingRuleDeletion === null || pendingRuleDeletion.isDeleting) return;
        const { parameterId, token } = pendingRuleDeletion;
        this.#dispatch(startDeleteRuleAction());
        try {
            await deleteRiskParameter(firmId, parameterId);
        } catch (error) {
            log.error(`Deleting LAB risk rule ${parameterId} failed for firm ${firmId}`, {
                error,
                firmId,
                parameterId,
            });
            if (this.#abort.signal.aborted) return;
            this.#dispatch(finishDeleteRuleAction(token));
            this.#store.dispatch({
                type: 'toast::enqueue',
                toast: {
                    message: t(
                        'admin::lab::risk-settings::delete-rule-error',
                        'Deleting the rule failed. Please try again.',
                    ),
                    group: 'default',
                    variant: 'error',
                },
            });
            return;
        }
        if (this.#abort.signal.aborted) return;
        this.#dispatch(finishDeleteRuleAction(token));
        await this.#refreshRiskQueries(firmId);
    }

    setAdvancedFilter(filter: AdvancedRuleFilter): void {
        this.#dispatch(setAdvancedFilterAction(filter));
    }

    setAdvancedSearchInput(value: string): void {
        this.#dispatch(setAdvancedSearchInputAction(value));
    }

    downloadAdvancedRulesCsv(): void {
        if (typeof document === 'undefined') return;
        const rules = this.advancedRuleRows.value;
        if (rules.length === 0) return;
        downloadCsvRows(
            'lab-risk-advanced-rules',
            buildAdvancedRulesCsvRows(
                rules,
                {
                    id: t('admin::lab::risk-settings::col-rule-id', 'Rule ID'),
                    firm: t('admin::lab::risk-settings::col-firm', 'Firm'),
                    memberFirm: t('admin::lab::risk-settings::col-member-firm', 'Member Firm'),
                    account: t('admin::lab::risk-settings::col-account', 'Account'),
                    route: t('admin::lab::risk-settings::col-route', 'Route'),
                    provider: t('admin::lab::risk-settings::col-provider', 'Provider'),
                    symbol: t('admin::lab::risk-settings::col-symbol', 'Symbol'),
                    maxPrice: t('admin::lab::risk-settings::max-price', 'Max Price'),
                    maxSize: t('admin::lab::risk-settings::max-size', 'Max Order Size'),
                    maxOrderTotal: t('admin::lab::risk-settings::max-order-total', 'Max Order Total'),
                    maxDailySize: t('admin::lab::risk-settings::max-daily-size', 'Max Daily Size'),
                    enabled: t('admin::lab::risk-settings::col-enabled', 'Enabled'),
                },
                {
                    enabled: t('admin::lab::risk-settings::advanced-enabled', 'Enabled'),
                    disabled: t('admin::lab::risk-settings::advanced-disabled', 'Disabled'),
                },
            ),
        );
    }

    [Symbol.dispose](): void {
        this.#abort.abort();
        this.#disposableStack.dispose();
    }
}
